/**
 * Default-on for the Vue 3 crossover, one enterprise at a time. Everywhere else the new app is
 * opt-in (the "Use New App (Beta)" switch); for an enterprise whose owner turned on "Open the
 * new app by default for staff" (Settings > Features, `config/public.enableNewAppForStaff`),
 * staff are moved to it automatically and opt OUT instead, with `ff_ui=legacy`.
 *
 * The decision lives in this app rather than in `middleware.js` because the edge cannot tell
 * staff from customers before sign-in, and customers stay on this app.
 * Design: formfactories-vue3 docs/superpowers/specs/2026-10-12-vue3-default-on-staff-design.md,
 * and 2026-10-27-new-app-enterprise-toggle-design.md for the setting.
 */

/** The `config/public` key. formfactories-vue3's settings page writes the same one. */
export const NEW_APP_FOR_STAFF_FLAG_KEY = 'enableNewAppForStaff'

/** Whether this enterprise has opted its staff in. Only an explicit `true` counts. */
export function resolveNewAppForStaff (configPublic) {
  return configPublic?.[NEW_APP_FOR_STAFF_FLAG_KEY] === true
}

/**
 * The setting as this page load sees it: the first `config/public` snapshot decides, later ones
 * are ignored. The listener is live, so without this an owner's save would hard-navigate every
 * open staff tab, their own included while its writes are still in flight. A change takes
 * effect on the next page load instead.
 *
 * @param {boolean|null} latched the value taken so far, `null` before the first snapshot
 * @param {object|null} configPublic the snapshot's data, `null` when the doc does not exist
 */
export function latchNewAppForStaff (latched, configPublic) {
  return latched === null ? resolveNewAppForStaff(configPublic) : latched
}

/**
 * Routes that exist only in the new app. Opening one switches any staff member there, on every
 * enterprise and even over the `ff_ui=legacy` opt-out: asking for the page is the choice.
 */
export const NEW_APP_ONLY_ROUTES = ['Alerts']

/**
 * True when this browser should be switched to the new app without being asked. Pure: every
 * input is passed in, so the whole table is unit-tested.
 *
 * @param {object} inputs
 * @param {boolean} inputs.enterpriseOptedIn the ACTIVE enterprise has the setting on
 * @param {boolean} inputs.enterpriseConfigPending its `config/public` has not landed yet
 * @param {boolean} inputs.activeEnterpriseIsHosts the active enterprise is the one this HOST
 *   resolves to, not an enterprise override: the new app resolves from the host alone
 * @param {boolean} inputs.signedIn a user is signed in and not mid-logout
 * @param {boolean} inputs.staffAccessPending the staff read has not landed (access reads 0)
 * @param {boolean} inputs.isStaff staff as the NEW app defines it (the admin token claim, or a
 *   staff document with access >= 5); alumni are not pushed
 * @param {boolean} inputs.isNative the Capacitor app — no middleware in front of it
 * @param {boolean} inputs.hasVue3Cookie already opted in, yet this app is running: nothing is
 *   proxying this origin, so another push would loop
 * @param {boolean} inputs.hasLegacyCookie the opt-out
 * @param {boolean} [inputs.onNewAppOnlyRoute] the current route is in NEW_APP_ONLY_ROUTES
 */
export function shouldAutoOptIn ({
  enterpriseOptedIn, enterpriseConfigPending, activeEnterpriseIsHosts, signedIn,
  staffAccessPending, isStaff, isNative, hasVue3Cookie, hasLegacyCookie, onNewAppOnlyRoute = false,
}) {
  if (!signedIn || staffAccessPending) return false
  if (!isStaff) return false
  // The setting does not matter on a new-app-only route, so neither does waiting for it.
  if (!onNewAppOnlyRoute && (enterpriseConfigPending || !enterpriseOptedIn)) return false
  if (!activeEnterpriseIsHosts) return false
  if (hasVue3Cookie) return false
  if (hasLegacyCookie && !onNewAppOnlyRoute) return false
  if (isNative) return false
  return true
}
